import Badge from '@/common/components/Badge'
import Box from '@/common/components/Box'
import { CurrencyTextField } from '@/common/components/CurrencyTextField'
import { UnitTextField } from '@/common/components/UnitTextField'
import { unities, unitiesDecimals } from '@/common/constants/unities.constants'
import { formatCurrency, formatDecimalUnit } from '@/common/utils/formatters'
import { ProductCategoryDistributionItemLoadingState } from '@/features/governmentAllocations/components/GovernmentAllocationDistribution/ProductCategoryDistributionItemLoadingState'
import { ProductDistributionItem } from '@/features/governmentAllocations/components/GovernmentAllocationDistribution/ProductDistributionItem'
import { useGovernmentAllocationsAllowedCategoriesAndProducts } from '@/features/governmentAllocations/hooks/useGovernmentAllocationsAllowedCategoriesAndProducts'
import { GovernmentAllocationAllowedCategoriesAndProducts } from '@/features/governmentAllocations/types/governmentAllocation.type'
import { CompanyGovernmentAllocationProductCategoriesDistribution } from '@/features/governmentAllocations/types/governmentAllocationDistribution.type'
import { BoxesIcon } from 'lucide-react'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'

interface ProductCategoryDistributionItemProps {
  productCategoryDistribution: CompanyGovernmentAllocationProductCategoriesDistribution
  companyBranchId?: string
  isConsumptionControlledByQuantity?: boolean
}

export const ProductCategoryDistributionItem = ({
  productCategoryDistribution,
  companyBranchId,
  isConsumptionControlledByQuantity,
}: ProductCategoryDistributionItemProps) => {
  const { id } = useParams()

  const { data: allowedCategoriesAndProducts, isPending: isAllowedCategoriesAndProductsPending } =
    useGovernmentAllocationsAllowedCategoriesAndProducts<GovernmentAllocationAllowedCategoriesAndProducts>({
      governmentAllocationId: id,
      filters: { companyBranchId, productCategoryId: productCategoryDistribution?.product_category?.id },
    })

  const allowedCategory = useMemo(() => {
    return isAllowedCategoriesAndProductsPending
      ? undefined
      : allowedCategoriesAndProducts['categories'][productCategoryDistribution?.product_category?.id]
  }, [isAllowedCategoriesAndProductsPending])

  const currentValue = isConsumptionControlledByQuantity
    ? productCategoryDistribution?.total_quantity
    : productCategoryDistribution?.total_value

  const allocated = Number(isConsumptionControlledByQuantity ? allowedCategory?.allocated_quantity : allowedCategory?.allocated_total) || 0
  const available = Number(isConsumptionControlledByQuantity ? allowedCategory?.available_quantity : allowedCategory?.available_total) || 0
  const distributed =
    Number(isConsumptionControlledByQuantity ? allowedCategory?.distributed_quantity : allowedCategory?.distributed_total) || 0

  const availableForAllocation =
    Math.min(
      Number(currentValue) || 0,
      Number(isConsumptionControlledByQuantity ? productCategoryDistribution?.total_quantity : productCategoryDistribution?.total_value) || 0,
    ) + available

  const productCategoryUnit = useMemo(() => {
    const firstProductUnit = (productCategoryDistribution?.products_distributions || [])[0]?.product.unit_for_quantity

    return (productCategoryDistribution?.products_distributions || []).every(
      (p) => p.product.unit_for_quantity === firstProductUnit,
    )
      ? firstProductUnit
      : undefined
  }, [(productCategoryDistribution?.products_distributions || []).length])

  if (isAllowedCategoriesAndProductsPending)
    return (
      <ProductCategoryDistributionItemLoadingState
        productsDistributionsLength={productCategoryDistribution?.products_distributions?.length || 0}
      />
    )

  return (
    <Box className="flex flex-col gap-4">
      <Badge
        Icon={BoxesIcon}
        iconProps={{ className: 'size-6', strokeWidth: 1 }}
        text={allowedCategory?.display_name || ''}
        className="rounded-sm flex bg-neutral-op-8 w-fit flex-row-reverse overflow-hidden !p-0 pe-2 gap-0"
        overwriteClassName
        contentClassName="px-3 py-2"
      />

      <div className="flex flex-col gap-1">
        {isConsumptionControlledByQuantity ? (
          <UnitTextField
            label="Quantidade limite"
            decimals={productCategoryUnit ? unitiesDecimals[productCategoryUnit] : 2}
            unit={productCategoryUnit ? unities[productCategoryUnit] : undefined}
            value={
              productCategoryDistribution?.total_quantity
                ? formatDecimalUnit(Number(productCategoryDistribution.total_quantity), productCategoryUnit)
                : ''
            }
            disabled
          />
        ) : (
          <CurrencyTextField
            label="Valor limite"
            value={
              productCategoryDistribution?.total_value
                ? formatCurrency(Number(productCategoryDistribution.total_value))
                : ''
            }
            disabled
          />
        )}

        <div className="flex gap-3">
          <p className="italic text-xs text-white-400">
            {isConsumptionControlledByQuantity ? 'Quantidade maxima: ' : 'Total máximo: '}
            <span className="whitespace-nowrap">
              {isConsumptionControlledByQuantity
                ? formatDecimalUnit(availableForAllocation, productCategoryUnit)
                : formatCurrency(availableForAllocation)}
            </span>
          </p>

          <p className="italic text-xs text-white-400">
            {isConsumptionControlledByQuantity ? 'Quantidade disponível: ' : 'Total disponível: '}
            <span className="whitespace-nowrap">
              {isConsumptionControlledByQuantity ? formatDecimalUnit(available, productCategoryUnit) : formatCurrency(available)}
            </span>
          </p>

          <p className="italic text-xs text-white-400">
            {isConsumptionControlledByQuantity ? 'Quantidade empenhada: ' : 'Total empenhado: '}
            <span className="whitespace-nowrap">
              {isConsumptionControlledByQuantity
                ? allocated
                  ? formatDecimalUnit(allocated, productCategoryUnit)
                  : '--'
                : allocated
                  ? formatCurrency(allocated)
                  : '--'}
            </span>
          </p>

          <p className="italic text-xs text-white-400">
            {isConsumptionControlledByQuantity ? 'Quantidade distribuída em todas filiais: ' : 'Total distribuído em todas filiais: '}
            <span className="whitespace-nowrap">
              {isConsumptionControlledByQuantity
                ? distributed
                  ? formatDecimalUnit(distributed, productCategoryUnit)
                  : '--'
                : distributed
                  ? formatCurrency(distributed)
                  : '--'}
            </span>
          </p>
        </div>
      </div>

      {productCategoryDistribution?.products_distributions?.map((productDistribution) => (
        <ProductDistributionItem
          key={productDistribution?.id}
          productDistribution={productDistribution}
          allowedProduct={allowedCategoriesAndProducts['products'][productDistribution?.product?.id]}
          isConsumptionControlledByQuantity={isConsumptionControlledByQuantity}
        />
      ))}
    </Box>
  )
}
